import React, { useState } from "react";
import { Trophy, Crown, Star, Flame, Medal, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { LeaderboardEntry } from "@/hooks/useExhibitionEvents";

interface ExhibitionLeaderboardProps {
  mostRequestedBooths: LeaderboardEntry[];
  topHosts: LeaderboardEntry[];
  popularExhibitors: LeaderboardEntry[];
  loading?: boolean;
}

type LeaderboardTab = "most_requested" | "top_host" | "popular_exhibitor";

const TAB_CONFIG: Record<LeaderboardTab, { label: string; icon: React.ReactNode; color: string }> = {
  most_requested: {
    label: "Most Requested",
    icon: <Flame className="w-4 h-4" />,
    color: "from-orange-500 to-amber-500",
  },
  top_host: {
    label: "Top Host",
    icon: <Crown className="w-4 h-4" />,
    color: "from-purple-500 to-violet-500",
  },
  popular_exhibitor: {
    label: "Popular Exhibitor",
    icon: <Star className="w-4 h-4" />,
    color: "from-pink-500 to-rose-500",
  },
};

const RANK_STYLES: Record<number, { bg: string; ring: string; icon: React.ReactNode }> = {
  1: {
    bg: "bg-gradient-to-br from-amber-400 to-yellow-500",
    ring: "ring-2 ring-amber-300",
    icon: <Trophy className="w-4 h-4 text-amber-500" />,
  },
  2: {
    bg: "bg-gradient-to-br from-gray-300 to-gray-400",
    ring: "ring-2 ring-gray-300",
    icon: <Medal className="w-4 h-4 text-gray-500" />,
  },
  3: {
    bg: "bg-gradient-to-br from-amber-600 to-orange-600",
    ring: "ring-2 ring-amber-600/50",
    icon: <Medal className="w-4 h-4 text-amber-600" />,
  },
};

const ExhibitionLeaderboard: React.FC<ExhibitionLeaderboardProps> = ({
  mostRequestedBooths,
  topHosts,
  popularExhibitors,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>("most_requested");

  const getLeaderboardData = () => {
    switch (activeTab) {
      case "most_requested":
        return mostRequestedBooths;
      case "top_host":
        return topHosts;
      case "popular_exhibitor":
        return popularExhibitors;
      default:
        return [];
    }
  };

  const data = getLeaderboardData();

  return (
    <div className="mx-4 mt-4 rounded-2xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/40 dark:border-gray-700/40">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold text-foreground">Leaderboards</h3>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {(Object.entries(TAB_CONFIG) as [LeaderboardTab, typeof TAB_CONFIG[LeaderboardTab]][]).map(
            ([key, config]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                  activeTab === key
                    ? `bg-gradient-to-r ${config.color} text-white shadow-md`
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                )}
              >
                {config.icon}
                {config.label}
              </button>
            )
          )}
        </div>
      </div>

      {/* Leaderboard list */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 rounded-full border-4 border-pop border-t-transparent animate-spin" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">No data yet</p>
            <p className="text-xs text-muted-foreground/70">
              Leaderboards update during live events
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.slice(0, 5).map((entry) => {
              const rankStyle = RANK_STYLES[entry.rank];
              const isTopThree = entry.rank <= 3;

              return (
                <div
                  key={`${entry.user_id}-${entry.booth_id || "user"}`}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl transition-all",
                    isTopThree
                      ? "bg-gradient-to-r from-amber-50/50 to-yellow-50/50 dark:from-amber-900/20 dark:to-yellow-900/20"
                      : "bg-muted/30"
                  )}
                >
                  {/* Rank */}
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                      rankStyle?.bg || "bg-muted",
                      rankStyle?.ring,
                      isTopThree ? "text-white" : "text-muted-foreground"
                    )}
                  >
                    {entry.rank}
                  </div>

                  {/* Avatar */}
                  <div className="relative">
                    {entry.user_photo ? (
                      <img
                        src={entry.user_photo}
                        alt={entry.user_name}
                        className={cn(
                          "w-10 h-10 rounded-full object-cover",
                          isTopThree && "ring-2 ring-amber-400"
                        )}
                      />
                    ) : (
                      <div className={cn(
                        "w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center",
                        isTopThree && "ring-2 ring-amber-400"
                      )}>
                        <span className="text-sm font-medium text-foreground">
                          {entry.user_name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      </div>
                    )}
                    {isTopThree && rankStyle?.icon && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow">
                        {rankStyle.icon}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {entry.user_name}
                    </p>
                    {entry.booth_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {entry.booth_name}
                      </p>
                    )}
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    <p className="font-bold text-lg text-foreground">
                      {entry.score}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activeTab === "most_requested" && "requests"}
                      {activeTab === "top_host" && "visitors"}
                      {activeTab === "popular_exhibitor" && "pops"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitionLeaderboard;
